import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, CheckCircle } from 'lucide-react';

export default function AdmissionFormPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Student Information
    studentName: '',
    dob: '',
    gender: '',
    classApplying: '',
    previousSchool: '',
    
    // Parent Information
    fatherName: '',
    motherName: '',
    contactNumber: '',
    alternateNumber: '',
    email: '',
    
    // Address
    addressLine1: '',
    city: '',
    state: '',
    pinCode: '',
    
    // Consent
    termsAccepted: false,
    dataConsent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAccepted || !formData.dataConsent) {
      toast.error('Please accept all terms and conditions');
      return;
    }

    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Application submitted successfully! You will receive a confirmation email shortly.');
      
      // Reset form
      setFormData({
        studentName: '', dob: '', gender: '', classApplying: '', previousSchool: '',
        fatherName: '', motherName: '', contactNumber: '', alternateNumber: '', email: '',
        addressLine1: '', city: '', state: '', pinCode: '',
        termsAccepted: false, dataConsent: false
      });
    } catch (error) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-950 text-white" data-testid="admission-form-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Online Admission Form</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Take the first step towards excellence. Fill out the form below to apply for admission.
          </p>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Important Notice */}
          <GlassCard className="mb-8 bg-amber-50 border-2 border-amber-200">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-amber-900 mb-2">Important Information</h3>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• Fill all required fields carefully</li>
                  <li>• Keep documents ready for upload (Birth Certificate, Previous School Certificate)</li>
                  <li>• You will receive a confirmation email within 24 hours</li>
                  <li>• Admission office: 9243639227 | Email: nsfschool123@gmail.com</li>
                </ul>
              </div>
            </div>
          </GlassCard>

          <form onSubmit={handleSubmit}>
            {/* Student Information */}
            <GlassCard className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Student Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                  <Input
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleChange}
                    placeholder="Enter student's full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date of Birth *</label>
                  <Input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Class Applying For *</label>
                  <select
                    name="classApplying"
                    value={formData.classApplying}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Class</option>
                    <option value="nursery">Nursery</option>
                    <option value="lkg">LKG</option>
                    <option value="ukg">UKG</option>
                    <option value="1">Class 1</option>
                    <option value="2">Class 2</option>
                    <option value="3">Class 3</option>
                    <option value="4">Class 4</option>
                    <option value="5">Class 5</option>
                    <option value="6">Class 6</option>
                    <option value="7">Class 7</option>
                    <option value="8">Class 8</option>
                    <option value="9">Class 9</option>
                    <option value="10">Class 10</option>
                    <option value="11">Class 11</option>
                    <option value="12">Class 12</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Previous School (if any)</label>
                  <Input
                    name="previousSchool"
                    value={formData.previousSchool}
                    onChange={handleChange}
                    placeholder="Enter previous school name"
                  />
                </div>
              </div>
            </GlassCard>

            {/* Parent/Guardian Information */}
            <GlassCard className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Parent / Guardian Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Father's Name *</label>
                  <Input
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleChange}
                    placeholder="Enter father's name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mother's Name *</label>
                  <Input
                    name="motherName"
                    value={formData.motherName}
                    onChange={handleChange}
                    placeholder="Enter mother's name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contact Number *</label>
                  <Input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    placeholder="Enter primary contact number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Alternate Number</label>
                  <Input
                    type="tel"
                    name="alternateNumber"
                    value={formData.alternateNumber}
                    onChange={handleChange}
                    placeholder="Enter alternate number"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>
            </GlassCard>

            {/* Address */}
            <GlassCard className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Address Details</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Address Line 1 *</label>
                  <Input
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleChange}
                    placeholder="House No., Street, Area"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">City *</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">State *</label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Enter state"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Pin Code *</label>
                  <Input
                    name="pinCode"
                    value={formData.pinCode}
                    onChange={handleChange}
                    placeholder="Enter pin code"
                    required
                  />
                </div>
              </div>
            </GlassCard>

            {/* Documents Upload */}
            <GlassCard className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Document Upload</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Birth Certificate *</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-500 mt-1">PDF, JPG or PNG (Max 5MB)</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Passport Photo *</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-500 mt-1">JPG or PNG (Max 2MB)</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Previous School Certificate</label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-slate-500 mt-1">PDF, JPG or PNG (Max 5MB)</p>
                  </div>
                </div>
              </div>
            </GlassCard>

            {/* Consent */}
            <GlassCard className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Terms & Consent</h2>
              <div className="space-y-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 text-blue-900 border-slate-300 rounded focus:ring-blue-500"
                    required
                  />
                  <span className="text-sm text-slate-700">
                    I accept the <a href="#" className="text-blue-900 font-semibold hover:underline">Terms and Conditions</a> and school policies
                  </span>
                </label>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="dataConsent"
                    checked={formData.dataConsent}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 text-blue-900 border-slate-300 rounded focus:ring-blue-500"
                    required
                  />
                  <span className="text-sm text-slate-700">
                    I consent to the processing of my personal data as per the <a href="#" className="text-blue-900 font-semibold hover:underline">Privacy Policy</a>
                  </span>
                </label>
              </div>
            </GlassCard>

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                disabled={loading}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-6 px-12 text-lg"
              >
                {loading ? 'Submitting Application...' : 'Submit Application'}
              </Button>
            </div>
          </form>

          {/* Contact Support */}
          <div className="mt-8 text-center">
            <p className="text-slate-600">
              Need help? Contact us at{' '}
              <a href="tel:9243639227" className="text-blue-900 font-semibold hover:underline">
                9243639227
              </a>
              {' '}or{' '}
              <a href="mailto:nsfschool123@gmail.com" className="text-blue-900 font-semibold hover:underline">
                nsfschool123@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}